/**
 * /checkins/{sessionNumber} — resolve a check-in from the number a person
 * actually holds.
 *
 * The session number (`YYYYMMDDNN`, e.g. 2026092702) is what reception prints
 * on the slip and what the customer quotes back on the phone, so it — not the
 * Dataverse GUID — is the reference anyone types or pastes. This page trades
 * it for the session id and redirects to the canonical
 * `/check-in/session/{id}` deep link, which is where every other entry point
 * into a session already lands.
 *
 * `replace` rather than a push, so Back goes where the person came from
 * instead of bouncing off the resolver.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Container, Stack } from '@mui/material';

import { fetchCheckInSessionByNumber } from '../services/checkInApi';

export interface CheckInByNumberPageProps {
  sessionNumber: string;
}

export const CheckInByNumberPage: React.FC<CheckInByNumberPageProps> = ({ sessionNumber }) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);

    fetchCheckInSessionByNumber(sessionNumber, controller.signal)
      .then(session => {
        if (controller.signal.aborted) return;
        if (!session) {
          setError(`No check-in found with reference ${sessionNumber}.`);
          return;
        }
        navigate(`/check-in/session/${session.id}`, { replace: true });
      })
      .catch(err => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to look up check-in');
      });

    return () => controller.abort();
  }, [sessionNumber, navigate]);

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Stack spacing={2}>
          <Alert severity="error">{error}</Alert>
          <Box>
            <Button variant="outlined" onClick={() => navigate('/check-ins')}>
              Back to Check-Ins
            </Button>
          </Box>
        </Stack>
      </Container>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50dvh' }}
    >
      <CircularProgress />
    </Box>
  );
};

export default CheckInByNumberPage;
